import { useGetPagesQuery } from 'api/siteApi';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Tabs from 'ui/components/Tabs';
import { enterpriseClients } from '../../hooks/use-enterprise-account';
import { usePermissions } from '../../hooks/use-permissions';
import { getSelectedSiteCollaborateInfo, getSelectedSiteId } from '../../store/siteInfoSlice';
import { Pages } from './pages/pages';
import { Tasks } from './tasks/tasks';

export const Selector = ({ isLoadingTasks }: { isLoadingTasks: boolean }) => {
  const siteId = useSelector(getSelectedSiteId);
  const collaborateInfo = useSelector(getSelectedSiteCollaborateInfo);
  const { isPermitted } = usePermissions();
  const { data, isLoading, isSuccess } = useGetPagesQuery(
    { siteId: siteId || '' },
    { skip: !siteId }
  );
  const { t } = useTranslation('collaborate');

  const [activeTab, setActiveTab] = useState<string>('tasks');
  const [isInitialized, setIsInitialized] = useState(false);

  const canAccessTasks = isPermitted("collaborate-sidebar-task-tab");
  const canAccessPages = isPermitted("collaborate-sidebar-pages-tab");

  // Initialize tab based on business logic
  useEffect(() => {
    if (isSuccess && data?.data?.pages?.page && collaborateInfo?.result && !isInitialized) {
      const isEnterpriseAccount = enterpriseClients.includes(collaborateInfo.result.account_id);
      const pageCount = data.data.pages.page.length;

      // Check for page click URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const fromPageClick = urlParams.get('fromPageClick') === 'true';

      if (fromPageClick) {
        setActiveTab('tasks');
        // Clean up URL parameter
        urlParams.delete('fromPageClick');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);
      } else if (isEnterpriseAccount || pageCount <= 1) {
        setActiveTab('tasks');
      } else {
        setActiveTab('pages');
      }

      setIsInitialized(true);
    }
  }, [isSuccess, data, collaborateInfo, isInitialized]);

  // Early returns for permission-based rendering
  if (!canAccessTasks && !canAccessPages) {
    return null;
  }

  if (canAccessTasks && !canAccessPages) {
    return (
      <div className="p-2">
        {siteId && <Tasks pages={data?.data?.pages?.page} isLoadingTasks={isLoadingTasks} />}
      </div>
    );
  }

  if (!canAccessTasks && canAccessPages) {
    return (
      <div className="p-2">
        {siteId && <Pages siteId={siteId} pages={data?.data?.pages?.page} isLoading={isLoading} isSuccess={isSuccess} />}
      </div>
    );
  }

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="p-2">
      <Tabs.List className="grid-cols-2 border-border">
        {canAccessTasks && (
          <Tabs.Trigger value="tasks">
            {t('sidebar.tasks', 'Tasks')}
          </Tabs.Trigger>
        )}
        {canAccessPages && (
          <Tabs.Trigger value="pages">
            {t('sidebar.pages', 'Pages')}
          </Tabs.Trigger>
        )}
      </Tabs.List>
      {canAccessTasks && (
        <Tabs.Content
          value="tasks"
          className="border-0 bg-transparent p-0 shadow-none"
        >
          {siteId && <Tasks pages={data?.data?.pages?.page} isLoadingTasks={isLoadingTasks} />}
        </Tabs.Content>
      )}
      {canAccessPages && (
        <Tabs.Content
          value="pages"
          className="border-0 bg-transparent p-0 shadow-none"
        >
          {siteId && <Pages siteId={siteId} pages={data?.data?.pages?.page} isLoading={isLoading} isSuccess={isSuccess} />}
        </Tabs.Content>
      )}
    </Tabs>
  );
};